'use client';

import { useCallback } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useShallow } from 'zustand/react/shallow';

import { ISanitizedCompany } from 'types/company';

import { useUser, useUserPermissions } from 'hooks/auth/user';

import { NewStoryButton } from 'components/creator-toolkit/NewStoryButton';

import { useHeaderActionsStore } from 'stores/headerActionsStore';

import { SearchForm } from './SearchForm';
import { SearchTrigger } from './SearchTrigger';
import { UserMenu } from './UserMenu';

interface IProps {
  company: ISanitizedCompany;
}

export const LayoutClient = ({ company }: IProps) => {
  const { user } = useUser();
  const { canCreateStories, canEditUsers } = useUserPermissions(company);
  const router = useRouter();
  const { actions, hideSearch } = useHeaderActionsStore(
    useShallow(state => ({ actions: state.actions, hideSearch: state.hideSearch })),
  );

  const handleSearch = useCallback(
    ({ term }: { term: string }) => {
      const url = new URL(window.location.href);

      const oldTerm = url.searchParams.get('term') || '';

      if (term === oldTerm) {
        return;
      }

      if (term) {
        url.searchParams.set('term', term);
      } else {
        url.searchParams.delete('term');
      }

      router.push(url.toString());
    },
    [router],
  );

  return (
    <header className="fixed left-0 right-0 top-0 z-[1000000000] border-b bg-culturedPearlGrey dark:border-b-white/10 dark:bg-mineShaftGrey">
      <div className="flex flex-row items-center justify-between px-3 py-1 sm:px-5 sm:py-4">
        <Link
          href={`/${company.slug}`}
          className="relative z-20 block h-[50px] w-full max-w-[150px] dark:hidden sm:max-w-[200px]"
        >
          <Image
            src={company.logoUrl}
            alt={`${company.name}'s logo`}
            fill
            sizes="(max-width: 640px) 150px, 200px"
            preload
            style={{ objectFit: 'contain', objectPosition: 'left center' }}
          />
        </Link>

        <Link
          href={`/${company.slug}`}
          className="relative z-20 hidden h-[50px] w-full max-w-[150px] dark:block sm:max-w-[200px]"
        >
          <Image
            src={company.logoDarkUrl}
            alt={`${company.name}'s logo`}
            fill
            sizes="(max-width: 640px) 150px, 200px"
            preload
            style={{ objectFit: 'contain', objectPosition: 'left center' }}
          />
        </Link>

        {!actions && !hideSearch && (
          <div className="z-30 mx-4 ml-auto h-full lg:absolute lg:inset-0 lg:z-10 lg:flex lg:items-center lg:justify-center">
            <SearchForm
              placeholder="Topic, colleague or keyword"
              includeMobile
              onSearch={term => handleSearch({ term })}
            />
          </div>
        )}

        <div className="relative z-20 ml-auto mr-4 h-full flex-shrink-0">
          <div className="flex">
            {actions ?? (
              <>
                <SearchTrigger />

                {canCreateStories && <NewStoryButton company={company} />}
              </>
            )}
          </div>
        </div>

        {!!user && (
          <div className="relative z-20 flex flex-shrink-0 flex-row items-center sm:z-30">
            <UserMenu
              company={company}
              user={user}
              canCreateStories={canCreateStories}
              canEditUsers={canEditUsers}
            />
          </div>
        )}
      </div>
    </header>
  );
};
